'use client';

import { useTranslations } from 'next-intl';
import { CheckCircle, Clock, RotateCcw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { ContainerRepairStatus, ContainerTransfer } from '../types';

export const REPAIR_STATUS_STYLES: Record<
  ContainerRepairStatus,
  { className: string; icon: typeof CheckCircle }
> = {
  waiting: {
    className:
      'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
    icon: Clock
  },
  completed: {
    className:
      'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400',
    icon: CheckCircle
  },
  returned: {
    className:
      'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400',
    icon: RotateCcw
  }
};

interface ContainerRepairHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  containerLabel?: string;
  transfers: ContainerTransfer[];
}

/**
 * Read-only list of every repair cycle a container went through, newest
 * first. Shared by the table listing, grid card, and compact row so all
 * three views render identical history detail.
 */
export function ContainerRepairHistoryDialog({
  open,
  onOpenChange,
  containerLabel,
  transfers
}: ContainerRepairHistoryDialogProps) {
  const t = useTranslations('ocr');

  const statusLabel = (status: ContainerRepairStatus) =>
    status === 'waiting'
      ? t('repairStatusWaiting')
      : status === 'completed'
        ? t('repairStatusCompleted')
        : t('repairStatusReturned');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>
            {t('repairHistoryTitle')}
            {containerLabel ? ` — ${containerLabel}` : ''}
          </DialogTitle>
        </DialogHeader>
        {transfers.length === 0 ? (
          <p className='text-muted-foreground py-4 text-sm'>
            {t('repairHistoryEmpty')}
          </p>
        ) : (
          <div className='max-h-[60vh] space-y-3 overflow-y-auto'>
            {transfers.map((transfer, index) => {
              const style = REPAIR_STATUS_STYLES[transfer.repair_status];
              const Icon = style.icon;
              return (
                <div
                  key={index}
                  className='space-y-2 rounded-md border p-3 text-sm'
                >
                  <div className='flex items-center justify-between gap-2'>
                    <Badge
                      variant='outline'
                      className={cn('gap-1', style.className)}
                    >
                      <Icon className='h-3 w-3' />
                      {statusLabel(transfer.repair_status)}
                    </Badge>
                    <span className='text-muted-foreground text-xs'>
                      {transfer.transferred_at
                        ? new Date(transfer.transferred_at).toLocaleDateString(
                            'en-US',
                            {
                              day: '2-digit',
                              month: 'short',
                              year: '2-digit'
                            }
                          )
                        : '-'}
                    </span>
                  </div>
                  <div>
                    <p className='text-muted-foreground text-xs'>
                      {t('reasonForSending')}
                    </p>
                    <p>{transfer.notes || '-'}</p>
                    {transfer.creator?.name && (
                      <p className='text-muted-foreground mt-0.5 text-xs'>
                        {t('sentBy')}{' '}
                        <span className='font-medium'>
                          {transfer.creator.name}
                        </span>
                      </p>
                    )}
                  </div>
                  <div>
                    <p className='text-muted-foreground text-xs'>
                      {t('repairComment')}
                    </p>
                    <p
                      className={cn(
                        !transfer.repair_comment && 'text-muted-foreground'
                      )}
                    >
                      {transfer.repair_comment || t('noCommentYet')}
                    </p>
                    {transfer.confirmed_by?.name && (
                      <p className='text-muted-foreground mt-0.5 text-xs'>
                        {t('confirmedBy')}{' '}
                        <span className='font-medium'>
                          {transfer.confirmed_by.name}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
